import React, { memo, useMemo } from 'react'
import MenuBrowser from './MenuBrowser'
import { motion } from 'framer-motion'
import { getNavBrowseAllProps } from '@/domain/useCases/navBar/getNavBrowseAllProps'
import { NavHelpItem } from './NavHelpItem'
import {
  ABOUT_US_TEXT,
  ADVISORY_BOARD_TEXT,
  CAREERS_TEXT,
  CLAIM_PROFILE_TEXT,
  CONTACT_US_TEXT,
  Containers,
  FOR_TREATMENT_PROVIDERS_TEXT,
  FOR_TREATMENT_SEEKERS_TEXT,
  OUR_CRITERIA_TEXT
} from '@/domain/useCases/navBar/constants'

const conVariants = {
  hidden: {
    opacity: 1,
    translateX: '300px',
    transition: {
      duration: 0.2,
      delay: 0.2
    }
  },
  animate: {
    opacity: 1,
    translateX: '0px',
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    translateX: '300px',
    transition: {
      duration: 0.2
    }
  }
}

const AboutMenu = ({ setPage, container }) => {
  const containerContent = useMemo(() => {
    if (!container) return null
    const browseButtonProps = getNavBrowseAllProps(container)
    switch (container) {
      case Containers.ABOUT_US:
        return (
          <>
            <NavHelpItem
              title="About Us"
              bodyText={ABOUT_US_TEXT}
              browseButtonProps={browseButtonProps}
            />
            <NavHelpItem
              title="Advisory Board"
              bodyText={ADVISORY_BOARD_TEXT}
              browseButtonProps={{
                href: '/advisory-board/',
                label: 'Meet Our Advisory Board'
              }}
            />
          </>
        )
      case Containers.FOR_TREATMENT_PROVIDERS:
        return (
          <div className="flex flex-col gap-y-4 overflow-x-clip">
            <NavHelpItem
              title="For Treatment Providers"
              bodyText={FOR_TREATMENT_PROVIDERS_TEXT}
              browseButtonProps={browseButtonProps}
            />
          </div>
        )

      case Containers.EDIT_PROFILE:
        return (
          <NavHelpItem
            title="Edit Your Profile"
            bodyText=""
            browseButtonProps={{
              href: 'https://rehabpath.typeform.com/to/iuUtOK',
              label: 'Suggest Edits'
            }}
          />
        )
      case Containers.OUR_CRITERIA:
        return (
          <div className="flex gap-y-4 overflow-x-clip">
            <NavHelpItem
              title="How to Choose a Treatment Provider"
              bodyText={OUR_CRITERIA_TEXT}
              browseButtonProps={browseButtonProps}
            />
            <NavHelpItem
              title="Claim Your Profile"
              bodyText={CLAIM_PROFILE_TEXT}
              browseButtonProps={{
                href: 'https://providers.recovery.com',
                label: 'Claim Your Center'
              }}
            />
          </div>
        )
      case Containers.CONTACT_US:
        return (
          <>
            <NavHelpItem
              title="For Providers"
              bodyText={CONTACT_US_TEXT}
              browseButtonProps={browseButtonProps}
            />
            <NavHelpItem
              title="For Treatment Seekers"
              bodyText={FOR_TREATMENT_SEEKERS_TEXT}
              browseButtonProps={{ href: '', label: '' }}
            />
            <div className="dialog">
              <span className="text-gray-800">
                If you need emergency assistance, please contact the following
                helplines:
              </span>
              <ul className="ml-4 gap-y-2 text-gray-800">
                <li className="list-disc">
                  911 or your local emergency number
                </li>
                <li className="list-disc">
                  <a
                    className="link"
                    target="_blank"
                    href="https://www.samhsa.gov/find-help/helplines/national-helpline"
                    rel="noreferrer"
                  >
                    SAMHSA’s National Helpline
                  </a>{' '}
                  <a href="tel:1800662-4357">
                    {' '}
                    at 1-800-662-HELP (1-800-662-4357)
                  </a>
                </li>
                <li className="list-disc">
                  The{' '}
                  <a
                    className="link"
                    target="_blank"
                    href="https://988lifeline.org/"
                    rel="noreferrer"
                  >
                    National Suicide Prevention Lifeline
                  </a>{' '}
                  <a href="tel:988">at 988</a>
                </li>
              </ul>
            </div>
          </>
        )
      case Containers.CAREERS:
        return (
          <NavHelpItem
            title="Careers"
            bodyText={CAREERS_TEXT}
            browseButtonProps={browseButtonProps}
          />
        )
    }
  }, [container])

  return (
    <motion.div
      key="about-menu"
      variants={conVariants}
      initial="hidden"
      animate="animate"
    >
      <div className="flex h-full min-h-screen flex-col gap-y-6 px-6 pb-24">
        <MenuBrowser setPage={setPage} />

        {containerContent}
      </div>
    </motion.div>
  )
}

export default memo(AboutMenu)
